
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { PortalAuthProvider } from './contexts/PortalAuthContext';
import { AppLayout, AdminLayout, PortalLayout } from './components/layouts';
import Home from './pages/Home';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Login from './pages/Login';
import NotFound from './pages/NotFound';
import { ApplicationForm, RegisterCommunity } from './components/Forms';
import Applications from './pages/Admin/Applications';
import {CheckApplications, SpectApplications, WelcomeScreen } from './pages/Admin/index';
import CommunityDashboard from './pages/Admin/CommunityDashboard';
import Communities from './pages/Admin/Communities';
import Invoices from './pages/Admin/Invoices';
import PortalUsersList from './pages/Admin/PortalUsersList';
import PortalSettingsPage from './pages/Admin/PortalSettingsPage';
import SignatureSettings from './pages/Community/SignatureSettings';
import SystemStorage from './pages/Admin/SystemStorage';
import ChangePassword from './pages/Admin/ChangePassword';
import PrivateRoute from './routes/PrivateRoute';
import PortalPrivateRoute from './routes/PortalPrivateRoute';
import PortalLogin from './pages/Portal/PortalLogin';
import PortalResetPassword from './pages/Portal/PortalResetPassword';
import PortalChangePassword from './pages/Portal/PortalChangePassword';
import PortalDashboard from './pages/Portal/PortalDashboard';
import PortalAppDetails from './pages/Portal/PortalAppDetails';
import { ToastProvider } from './contexts/ToastContext';
import ToastNotification from './utils/ToastNotification'
import SoftLoginModal from "./components/Modals/SoftLoginModal";
import { LoadingProvider } from "./contexts/LoadingContext";
import GlobalSpinner from "./utils/GlobalSpinner";

const changeFavicon = (faviconPath) => {
  const link = document.querySelector("link[rel~='icon']");
  if (link) {
      link.href = faviconPath;
  } else {
      const newLink = document.createElement("link");
      newLink.rel = "icon";
      newLink.href = faviconPath;
      document.head.appendChild(newLink);
  }
};


const RouteTitleAndFavicon = () => {
  const location = useLocation();

  useEffect(() => {
      const routeSettings = {
          "/login": { favicon: "/favicon.ico", title: "SimpleSuite" },
          "/admin": { favicon: "/favicon.ico", title: "SimpleSuite - Admin" },
          "/community-portal": { favicon: "/favicon.ico", title: "SimpleSuite - Community Portal" },
          "/portal": { favicon: "/favicon.ico", title: "SimpleSuite - Applicant Portal" },
          "/": { favicon: "/favicon.ico", title: "Simplepermit ™" },
      };

      const defaultSettings = { favicon: "/favicon.ico", title: "Simplepermit ™" };

      // Get the appropriate settings based on the route
      const settings = Object.keys(routeSettings).find((route) => location.pathname.startsWith(route)) 
          ? routeSettings[Object.keys(routeSettings).find((route) => location.pathname.startsWith(route))]
          : defaultSettings;

      document.title = settings.title;
      changeFavicon(settings.favicon);
  }, [location]);

  return null;
};

function App() {
  const [showSoftLogin, setShowSoftLogin] = useState(false);

  return (
    <Router basename={process.env.PUBLIC_URL || ''}>
       <LoadingProvider>
       <ToastProvider>
      <AuthProvider>
      <PortalAuthProvider>
        <ToastNotification />
        <RouteTitleAndFavicon />
        {showSoftLogin && (
          <SoftLoginModal onClose={() => setShowSoftLogin(false)} />
        )}
        <Routes>
          <Route element={<AppLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/application" element={<ApplicationForm />} />
            <Route path="/register" element={<RegisterCommunity />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          </Route>
          <Route path="/login" element={<Login />} />

          {/* Applicant Portal Routes */}
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route path="/portal/reset-password" element={<PortalResetPassword />} />
          
          <Route element={<PortalPrivateRoute />}>
            <Route path="/portal/change-password" element={<PortalChangePassword />} />
            <Route element={<PortalLayout />}>
              <Route path="/portal" element={<PortalDashboard product="permit" />} />
              <Route path="/portal/dashboard" element={<PortalDashboard product="permit" />} />
              <Route path="/portal/permits" element={<PortalDashboard product="permit" />} />
              <Route path="/portal/check" element={<PortalDashboard product="check" />} />
              <Route path="/portal/spect" element={<PortalDashboard product="spect" />} />
              <Route path="/portal/application/:product/:id" element={<PortalAppDetails />} />
            </Route>
          </Route>

          <Route element={<PrivateRoute requiredRole="admin" />}> 
            <Route path="/admin" element={<AdminLayout />}>
              <Route path="dashboard" element={<WelcomeScreen />} />
              <Route path="applications" element={<Applications />} />
              <Route path="communities/:id" element={<CommunityDashboard />} /> 
              <Route path="communities" element={<Communities />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="portal-users" element={<PortalUsersList />} />
              <Route path="portal-settings" element={<PortalSettingsPage />} />
              <Route path="system-storage" element={<SystemStorage />} />
              <Route path="change-password" element={<ChangePassword />} />
              <Route path="simplecheck/application" element={<CheckApplications />} />
              <Route path="simplespect/application" element={<SpectApplications />} />
            </Route>
          </Route>
          <Route element={<PrivateRoute requiredRole="Community" />}>
            <Route path="/community-portal" element={<AdminLayout />}>
              <Route path="dashboard" element={<CommunityDashboard />} />
              <Route path="signatures" element={<SignatureSettings />} />
              <Route path="change-password" element={<ChangePassword />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </PortalAuthProvider>
      </AuthProvider>
      </ToastProvider>
      <GlobalSpinner />
       </LoadingProvider>
    </Router>
  );
}

export default App;
